/**
 * Architect-supplied facts — merged into live engine input with local audit
 * persistence (versioned revisions in localStorage per project).
 */
import type { EvaluatedElement, QuantityValue } from "@vera/rules";
import type { ViolationProvenance } from "@vera/shared";
import { create } from "zustand";
import {
  appendUserFactRevision,
  latestOverridesFromAudit,
  latestUserFactsForElement,
  type UserFactRevision,
} from "../lib/userFactAuditDb";

export type UserFactValue = QuantityValue | boolean | string;

interface UserFactStore {
  projectId: string | null;
  /** dbId → factKey → latest value (mirrors audit store) */
  overrides: Record<number, Record<string, UserFactValue>>;

  loadProject: (projectId: string) => void;
  setFact: (input: {
    projectId: string;
    dbId: number;
    factKey: string;
    label?: string;
    ruleId?: string;
    value: UserFactValue;
    copiedFromDbId?: number;
    copiedFromVersion?: number;
  }) => UserFactRevision;
  clearAll: () => void;
  getOverridesFor: (dbId: number) => Record<string, UserFactValue>;
  getAuditForElement: (dbId: number) => UserFactRevision[];
}

export const useUserFactStore = create<UserFactStore>((set, get) => ({
  projectId: null,
  overrides: {},

  loadProject: projectId => {
    const overrides = latestOverridesFromAudit(projectId);
    set({ projectId, overrides });
  },

  setFact: input => {
    const revision = appendUserFactRevision(input);
    set(s => ({
      projectId: input.projectId,
      overrides: {
        ...s.overrides,
        [input.dbId]: {
          ...s.overrides[input.dbId],
          [input.factKey]: input.value,
        },
      },
    }));
    return revision;
  },

  clearAll: () => set({ projectId: null, overrides: {} }),

  getOverridesFor: dbId => get().overrides[dbId] ?? {},

  getAuditForElement: dbId => {
    const pid = get().projectId;
    if (!pid) return [];
    return latestUserFactsForElement(pid, dbId);
  },
}));

export function mergeElementWithUserFacts(
  element: EvaluatedElement,
  userOverrides: Record<number, Record<string, UserFactValue>>,
  auditRevisions?: UserFactRevision[]
): EvaluatedElement {
  const extra = userOverrides[element.dbId];
  if ((!extra || Object.keys(extra).length === 0) && !auditRevisions?.length) {
    return element;
  }

  const facts = { ...element.facts };
  const factSources = { ...element.factSources };
  const auditByKey = new Map((auditRevisions ?? []).map(r => [r.factKey, r]));

  for (const [key, value] of Object.entries(extra ?? {})) {
    facts[key] = value;
    const rev = auditByKey.get(key);
    factSources[key] = rev
      ? rev.copiedFromDbId != null
        ? `Architect supplied v${rev.version} (copied from dbId ${rev.copiedFromDbId} v${rev.copiedFromVersion ?? "?"})`
        : `Architect supplied v${rev.version} (${formatWhen(rev.updatedAt)})`
      : "Architect supplied (session override)";
  }
  return { ...element, facts, factSources };
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function quantityToProvenance(
  value: UserFactValue
): { value: number; unit: string } | boolean | string {
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    "unit" in value
  ) {
    return { value: value.value, unit: value.unit };
  }
  return value;
}

/** Map audit revisions to violation provenance.userSupplied.facts */
export function userSuppliedProvenanceFacts(
  revisions: UserFactRevision[]
): NonNullable<ViolationProvenance["userSupplied"]>["facts"] {
  return revisions.map(r => ({
    factKey: r.factKey,
    label: r.label,
    value: quantityToProvenance(r.value),
    version: r.version,
    suppliedAt: r.suppliedAt,
    updatedAt: r.updatedAt,
  }));
}
